import { extractDevDetails } from './error-utils';

import { ForbiddenError, UnauthorizedError } from '@/_generated/api-clients/xp';

interface HasStatus {
  status: number;
}

/**
 * Check if an error is a 401 Unauthorized error.
 * Note: 403 Forbidden is NOT included as it indicates authorization (permissions),
 * not authentication (token) issues.
 */
const isUnauthorizedError = (err: unknown): err is UnauthorizedError => {
  if (typeof err === 'object' && err !== null && 'status' in err) {
    const e = err as HasStatus;
    return e.status === 401;
  }
  return false;
};

/**
 * Check if an error is a 403 Forbidden error.
 * This indicates authorization/permissions issues, not authentication.
 */
export const isForbiddenError = (err: unknown): err is ForbiddenError => {
  if (typeof err === 'object' && err !== null && 'status' in err) {
    const e = err as HasStatus;
    return e.status === 403;
  }
  return false;
};

interface NoAccessTokenError {
  message: 'No access token';
}

const isNoAccessTokenError = (err: unknown): err is NoAccessTokenError =>
  typeof err === 'object' &&
  err !== null &&
  'message' in err &&
  err.message === 'No access token';

/**
 * Known token-related error codes from OAuth/Okta responses.
 */
const TOKEN_RELATED_ERROR_CODES = [
  'invalid_token',
  'token_expired',
  'access_denied',
  'invalid_grant',
] as const;

/**
 * Check if a 401 error is specifically token-related.
 * This helps distinguish between "token is invalid/expired" (needs re-auth)
 * vs other 401s that might be API-specific.
 */
const hasTokenRelatedErrorCode = (err: unknown): boolean => {
  if (typeof err !== 'object' || err === null) {
    return false;
  }

  // Check various error shapes for token-related codes
  const errorObj = err as Record<string, unknown>;

  // Check error.errorCode (Okta SDK style)
  if (
    typeof errorObj.errorCode === 'string' &&
    TOKEN_RELATED_ERROR_CODES.includes(
      errorObj.errorCode as (typeof TOKEN_RELATED_ERROR_CODES)[number],
    )
  ) {
    return true;
  }

  // Check error.body?.error or error.body?.code (API response style)
  if (typeof errorObj.body === 'object' && errorObj.body !== null) {
    const body = errorObj.body as Record<string, unknown>;
    if (
      typeof body.error === 'string' &&
      TOKEN_RELATED_ERROR_CODES.includes(
        body.error as (typeof TOKEN_RELATED_ERROR_CODES)[number],
      )
    ) {
      return true;
    }
    if (
      typeof body.code === 'string' &&
      TOKEN_RELATED_ERROR_CODES.includes(
        body.code as (typeof TOKEN_RELATED_ERROR_CODES)[number],
      )
    ) {
      return true;
    }
  }

  // Check error.response?.data (axios-style)
  if (typeof errorObj.response === 'object' && errorObj.response !== null) {
    const response = errorObj.response as Record<string, unknown>;
    if (typeof response.data === 'object' && response.data !== null) {
      const data = response.data as Record<string, unknown>;
      if (
        typeof data.error === 'string' &&
        TOKEN_RELATED_ERROR_CODES.includes(
          data.error as (typeof TOKEN_RELATED_ERROR_CODES)[number],
        )
      ) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Check if an error indicates a token-related authentication problem
 * that requires re-authentication (logout/re-login).
 *
 * Returns true for:
 * - 401 errors with token-related error codes (invalid_token, token_expired, etc.)
 * - Explicit "No access token" errors
 *
 * Returns false for:
 * - 403 Forbidden (authorization, not authentication)
 * - 401 without token-related codes (could be API-specific auth issues)
 * - 5xx errors (server issues)
 */
export const isTokenRelatedAuthError = (err: unknown): boolean => {
  // "No access token" is always a token-related auth error
  if (isNoAccessTokenError(err)) {
    console.warn('No access token error detected - requires re-auth');
    return true;
  }

  // 401 with token-related error code requires re-auth
  if (isUnauthorizedError(err) && hasTokenRelatedErrorCode(err)) {
    console.warn('Token-related 401 error detected - requires re-auth', err);
    return true;
  }

  // For 401 without specific token codes, we still treat it as token-related
  // to maintain backward compatibility. However, we log this with enhanced
  // context to help identify cases where token detection could be improved.
  if (isUnauthorizedError(err)) {
    const errorDetails = extractDevDetails(err);

    // Log with structured context for easier Datadog filtering and analysis
    console.warn(
      '[TOKEN_DETECTION_FALLBACK] 401 error without specific token error code - treating as token-related for safety.',
      {
        category: 'auth_fallback',
        reason: 'no_token_error_code',
        status: 401,
        message:
          'This may indicate an API-specific auth issue rather than a token problem',
        errorDetails,
        recommendation:
          'Review error structure to add specific token error code if this is a genuine token issue',
      },
    );

    return true;
  }

  return false;
};
